// ── HPL Vehicle Lease Agreement — document engine ─────────────────────────────
// Builds the Hollypaul Leasing → driver vehicle lease as a paginated PDF (pdf-lib,
// the same lib the doc-scan flows use). The terms encode the agreed business rules:
//   • lease fee + insurance excess ($2,500 basic, plus age/inexperienced) carried in
//   • tyres: HPL covers fair wear only; driver pays punctures/cuts/kerb/abuse
//   • wear-and-tear return policy (fair wear excepted vs chargeable damage)
//   • insurance inclusions surfaced (hire car, windscreen nil-excess, towing, etc.)
// Source figures: Allianz Motor Fleet policy POL428BA + the PHB schedule.
//
// buildLeaseContractPdfBytes(data) → Uint8Array (a real PDF, ready to file/download).

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// GST-inclusive value of an ex-GST figure. The lease cap is stored ex-GST (that is the
// basis the RCTI nets it off gross on), but the business sets it as a round GST-INCLUSIVE
// price — e.g. $954.55 ex → $1,050.005, which is really the intended $1,050.00 incl. We
// snap that sub-cent GST-rounding artifact so the contract reads the clean inclusive figure.
export const gstInclusive = (n) => {
  const cents = Math.round((Number(n) || 0) * 1.1 * 100) / 100;
  const whole = Math.round(cents);
  return Math.abs(cents - whole) <= 0.011 ? whole : cents;
};
const fmtDate = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date)) return '____________';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Build the ordered content blocks. Each block: { kind, text, ... }.
// kind: 'title' | 'centre' | 'h' (heading) | 'p' (paragraph) | 'kv' (label/value) |
//       'rule' | 'gap' | 'sigline'
export function leaseContractBlocks(d) {
  const lessor = d.lessorName || 'HOLLYPAUL LEASING PTY LTD';
  const lessorAbn = d.lessorAbn || '[INSERT ABN]';
  // The Lessee (legal party) is the ABN holder — the company/business entity.
  // The individual driver is named as the person who signs on the entity's behalf.
  const lesseeEntity = (d.lessee?.entity || d.lessee?.name || '[LESSEE]').toUpperCase();
  const lesseeRep = d.lessee?.repName || d.lessee?.name || '';
  const lesseeAbn = d.lessee?.abn || '[ABN]';
  const hasRep = lesseeRep && lesseeRep.toUpperCase() !== lesseeEntity;
  const veh = d.vehicle || {};
  const vehDesc = [veh.year, veh.make, veh.model].filter(Boolean).join(' ') || '[VEHICLE]';
  const rego = veh.rego || '[REGISTRATION]';
  const state = d.state || 'New South Wales';
  // The lease fee is held GST-EXCLUSIVE (the same basis the RCTI uses — it nets the
  // lease off gross before applying GST), so show ex-GST and the inc-GST figure.
  // Two fee shapes, mirroring the RCTI's precedence (rate wins whenever it's set):
  //   • variable  — a % of the period's gross service fees, optionally capped;
  //   • fixed     — a flat dollar amount per 4-week period.
  const feeNum = Number(d.weeklyFee) || 0;
  const rateNum = Number(d.leaseRate) || 0;
  const capNum = Number(d.leaseCap) || 0;
  const isPct = rateNum > 0;
  const pctStr = `${(rateNum * 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })}%`;
  const capEx = fmtMoney(capNum);
  const capInc = fmtMoney(gstInclusive(capNum));
  const weeklyEx = feeNum ? fmtMoney(feeNum) : '$__________';
  const weeklyInc = feeNum ? fmtMoney(feeNum * 1.1) : '$__________';
  // Schedule 1 value + the phrasing woven into clause 2.1. The cap is stated GST-INCLUSIVE
  // first (that is the figure the business commits to), with the ex-GST basis in brackets.
  const feeSchedule = isPct
    ? `${pctStr} of the Lessee’s gross service fees per 4-week period${capNum > 0 ? `, capped at ${capInc} incl. GST (${capEx} ex GST)` : ''}`
    : (feeNum ? `${weeklyEx} plus GST (${weeklyInc} incl. GST)` : '$__________');
  const paymentSchedule = isPct
    ? 'Each 4-week period, calculated on gross and set off via the RCTI'
    : 'Every 4 weeks, in advance (via the RCTI)';
  const weekly = feeSchedule;
  const exBasic = fmtMoney(d.excessBasic ?? 2500);
  const exU21 = fmtMoney(d.excessAgeUnder21 ?? 850);
  const ex21 = fmtMoney(d.excessAge21to25 ?? 500);
  const exInexp = fmtMoney(d.excessInexp ?? 500);
  const term = d.term || 'Ongoing — until terminated under clause 13';

  const B = [];
  // (The branded header band is drawn by the PDF builder; blocks start at the parties.)
  B.push({ kind: 'gap' });
  B.push({ kind: 'centre', text: 'BETWEEN', size: 8 });
  B.push({ kind: 'centre', text: `${lessor}  ·  ABN ${lessorAbn}`, bold: true });
  B.push({ kind: 'centre', text: '("The Lessor")' });
  B.push({ kind: 'centre', text: 'AND' });
  B.push({ kind: 'centre', text: `${lesseeEntity}  ·  ABN ${lesseeAbn}`, bold: true });
  if (hasRep) B.push({ kind: 'centre', text: `represented by ${lesseeRep}`, size: 8.5 });
  B.push({ kind: 'centre', text: '("The Lessee")' });
  B.push({ kind: 'rule' });

  B.push({ kind: 'h', text: 'RECITALS' });
  B.push({ kind: 'p', text: 'A. The Lessor is the registered owner of the vehicle described in Schedule 1.' });
  B.push({ kind: 'p', text: 'B. The Lessee wishes to lease the vehicle for use in performing delivery services as a subcontractor to Hollypaul Recruitment Pty Ltd.' });
  B.push({ kind: 'p', text: 'C. The Lessor agrees to lease the vehicle to the Lessee on the terms and conditions set out in this Agreement.' });

  B.push({ kind: 'h', text: 'SCHEDULE 1 — VEHICLE & LEASE DETAILS' });
  B.push({ kind: 'kv', label: 'Year / Make / Model', value: vehDesc });
  B.push({ kind: 'kv', label: 'Registration', value: rego });
  B.push({ kind: 'kv', label: 'Odometer at start', value: '____________ km' });
  B.push({ kind: 'kv', label: 'Lease start date', value: fmtDate(d.startDate || new Date()) });
  B.push({ kind: 'kv', label: 'Lease term', value: term });
  B.push({ kind: 'kv', label: 'Lease fee (per 4 weeks)', value: weekly });
  B.push({ kind: 'kv', label: 'Payment', value: paymentSchedule });

  B.push({ kind: 'h', text: 'SCHEDULE 2 — RESPONSIBILITY FOR COSTS' });
  B.push({ kind: 'p', text: 'The following allocation of costs applies during the Lease Term:' });
  B.push({ kind: 'p', bold: true, text: 'The Lessor (Hollypaul Leasing) is responsible for:', gap: 2 });
  B.push({ kind: 'p', indent: 14, text: '•  Comprehensive vehicle insurance premium (Allianz Motor Fleet).' });
  B.push({ kind: 'p', indent: 14, text: '•  Scheduled servicing and major mechanical maintenance.' });
  B.push({ kind: 'p', indent: 14, text: '•  Vehicle registration and CTP.' });
  B.push({ kind: 'p', indent: 14, text: '•  Replacement of tyres worn through normal use (fair wear).' });
  B.push({ kind: 'p', bold: true, text: 'The Lessee (driver) is responsible for:', gap: 2 });
  B.push({ kind: 'p', indent: 14, text: '•  The lease fee (per 4-week period).' });
  B.push({ kind: 'p', indent: 14, text: '•  Fuel.' });
  B.push({ kind: 'p', indent: 14, text: '•  Tolls, traffic and parking fines, and infringements.' });
  B.push({ kind: 'p', indent: 14, text: '•  Cleaning and the interior/exterior condition of the Vehicle.' });
  B.push({ kind: 'p', indent: 14, text: '•  The insurance excess on any at-fault incident (see clause 6).' });
  B.push({ kind: 'p', indent: 14, text: '•  Daily pre-start checks and fluid / tyre-pressure top-ups.' });
  B.push({ kind: 'p', indent: 14, text: '•  Tyre damage from punctures, cuts, bursts, kerbing or misuse (excluded by the insurer).' });
  B.push({ kind: 'p', indent: 14, text: '•  Any damage not covered by the Lessor’s insurance arising from the Lessee’s negligence or misuse.' });

  B.push({ kind: 'h', text: 'TERMS AND CONDITIONS' });

  B.push({ kind: 'p', bold: true, text: '1. LEASE OF VEHICLE' });
  B.push({ kind: 'p', text: '1.1 The Lessor agrees to lease the vehicle described in Schedule 1 (the "Vehicle") to the Lessee for the Lease Term.' });
  B.push({ kind: 'p', text: '1.2 The Lessee acknowledges that ownership of the Vehicle remains with the Lessor at all times. The Lessee acquires no ownership interest in the Vehicle under this Agreement or at the end of the Lease Term.' });
  B.push({ kind: 'p', text: '1.3 The Lessee shall NOT retain or purchase the Vehicle at the end of the Lease Term. The Vehicle must be returned to the Lessor in accordance with clause 12.' });

  B.push({ kind: 'p', bold: true, text: '2. LEASE PAYMENTS' });
  B.push({ kind: 'p', text: isPct
    ? `2.1 The Lease Fee is ${pctStr} of the Lessee’s gross service fees earned in each 4-week period${capNum > 0 ? `, capped at ${capInc} including GST (${capEx} plus GST) per period` : ''}. It is calculated on the Lessee’s gross service fees for the period and set off against amounts payable to the Lessee, as described in clause 2.4.`
    : `2.1 The Lessee shall pay the Lease Fee specified in Schedule 1 (${weeklyEx} plus GST per 4-week period) to the Lessor, in advance, every 4 weeks.` });
  B.push({ kind: 'p', text: '2.2 Payments shall be made by direct debit, deduction from amounts payable under the Lessee’s subcontractor arrangement, or such other method as the Lessor nominates in writing.' });
  B.push({ kind: 'p', text: '2.3 If the Lessee fails to make any payment when due, the Lessor may, at its discretion, terminate this Agreement immediately under clause 13.' });
  B.push({ kind: 'p', text: '2.4 The Lease Fee is exclusive of GST. GST is applied at the prevailing rate and is accounted for through the Lessee’s 4-weekly Recipient-Created Tax Invoice (RCTI), under which the Lease Fee is set off against the Lessee’s service fees.' });

  B.push({ kind: 'p', bold: true, text: '3. USE OF VEHICLE' });
  B.push({ kind: 'p', text: '3.1 The Lessee may use the Vehicle solely for the performance of delivery and logistics services as a subcontractor.' });
  B.push({ kind: 'p', text: '3.2 The Lessee must not: (a) use the Vehicle for personal or recreational purposes without prior written approval from the Lessor; (b) allow any person other than the Lessee to drive the Vehicle without prior written approval from the Lessor; (c) use the Vehicle in any unlawful manner or in breach of any law; (d) remove the Vehicle from the agreed operating area without consent.' });
  B.push({ kind: 'p', text: '3.3 The Lessee must comply at all times with all applicable road transport legislation, fatigue management regulations, and Chain of Responsibility obligations.' });

  B.push({ kind: 'p', bold: true, text: '4. MAINTENANCE AND CARE' });
  B.push({ kind: 'p', text: '4.1 The Lessor is responsible for major mechanical maintenance and scheduled servicing of the Vehicle at intervals determined by the Lessor, and for replacement of tyres worn through normal use.' });
  B.push({ kind: 'p', text: '4.2 The Lessee is responsible for: (a) daily pre-start vehicle checks; (b) maintaining adequate tyre pressure and fluid levels (top-up only); (c) reporting any defect, damage or malfunction to the Lessor promptly and no later than the end of the same business day; (d) keeping the interior and exterior of the Vehicle clean and hygienic.' });
  B.push({ kind: 'p', text: '4.3 The Lessee is responsible for the cost of repairing or replacing tyres or wheel rims damaged by punctures, cuts, bursts, kerbing, potholes or misuse, such damage being excluded from the Lessor’s insurance.' });
  B.push({ kind: 'p', text: '4.4 The Lessee must not carry out or authorise any repair, modification or alteration to the Vehicle without the Lessor’s prior written consent.' });

  B.push({ kind: 'p', bold: true, text: '5. INSURANCE' });
  B.push({ kind: 'p', text: '5.1 The Lessor will maintain comprehensive vehicle insurance on the Vehicle (Allianz Motor Fleet, policy POL428BA).' });
  B.push({ kind: 'p', text: '5.2 That cover includes, subject to the policy terms: an emergency hire vehicle following an accident, towing and protection costs, windscreen / window glass repair with no excess, emergency repairs, and re-keying if keys are lost or stolen.' });
  B.push({ kind: 'p', text: '5.3 The insurance does NOT cover wear and tear, mechanical or electrical failure, or tyre/rim damage from punctures, cuts, bursts or braking. Those are dealt with under clauses 4 and 12.' });
  B.push({ kind: 'p', text: '5.4 The Lessee must not do anything that would void or prejudice the Lessor’s insurance, and must immediately notify the Lessor of any accident, theft or damage regardless of fault or severity.' });

  B.push({ kind: 'p', bold: true, text: '6. EXCESS PAYABLE — AT-FAULT INCIDENTS' });
  B.push({ kind: 'p', text: '6.1 Where the Lessee is at fault in an accident, collision or incident (an "At-Fault Incident"), the Lessee shall be liable to pay the insurance excess(es) applicable under the Lessor’s policy.' });
  B.push({ kind: 'p', text: `6.2 The excesses currently are: Basic Excess ${exBasic} per incident; an additional Age Excess of ${exU21} (driver under 21) or ${ex21} (driver aged 21–25); and an additional Inexperienced Driver Excess of ${exInexp} (driver 25 or over who has held an Australian licence for less than 2 years). These amounts may change on renewal of the policy, of which the Lessor will give 14 days’ notice.` });
  B.push({ kind: 'p', text: '6.3 "At-Fault" includes incidents where the Lessee is wholly or partly responsible as determined by the insurer or a court, was unlicensed, was in breach of this Agreement, or failed to report the incident as required under clause 5.4.' });
  B.push({ kind: 'p', text: '6.4 No excess is payable by the Lessee where the incident is not at-fault and the at-fault third party is identified, or for windscreen / window glass only claims.' });
  B.push({ kind: 'p', text: '6.5 The excess shall be deducted from the Lessee’s next payment due under their subcontractor arrangement with Hollypaul Recruitment Pty Ltd, or invoiced separately. The Lessee’s liability is not limited to the excess where damage exceeds the insured value, or where the insurer declines a claim due to the Lessee’s breach.' });

  B.push({ kind: 'p', bold: true, text: '7. TRAFFIC INFRINGEMENTS, FINES AND TOLLS' });
  B.push({ kind: 'p', text: '7.1 The Lessee is solely responsible for all traffic infringements, parking fines, tolls and other charges incurred during the Lease Term. Where the Lessor receives any such notice it will nominate the Lessee, and any administrative fee will be reimbursed by the Lessee within 7 days of demand.' });

  B.push({ kind: 'p', bold: true, text: '8. FUEL' });
  B.push({ kind: 'p', text: '8.1 The Lessee is responsible for all fuel costs and must use the correct fuel type for the Vehicle.' });

  B.push({ kind: 'p', bold: true, text: '9. REPRESENTATIONS AND WARRANTIES' });
  B.push({ kind: 'p', text: '9.1 The Lessee warrants that they hold a current, valid Australian driver’s licence appropriate for the class of Vehicle, will immediately notify the Lessor if their licence is suspended, cancelled or made conditional, and that all information provided to the Lessor is true and accurate.' });

  B.push({ kind: 'p', bold: true, text: '10. INDEMNITY' });
  B.push({ kind: 'p', text: '10.1 The Lessee indemnifies and holds harmless the Lessor and its related bodies corporate from and against all claims, losses, damages, liabilities, costs and expenses (including legal costs on a full indemnity basis) arising from the Lessee’s use of the Vehicle, any breach of this Agreement, any At-Fault Incident, or any act or omission of any person operating the Vehicle with the Lessee’s permission.' });

  B.push({ kind: 'p', bold: true, text: '11. NO TRANSFER' });
  B.push({ kind: 'p', text: '11.1 The Lessee must not assign, sublease or otherwise transfer any rights or obligations under this Agreement without the Lessor’s prior written consent.' });

  B.push({ kind: 'p', bold: true, text: '12. RETURN OF VEHICLE & WEAR AND TEAR' });
  B.push({ kind: 'p', text: '12.1 Upon expiry or termination of this Agreement, the Lessee must immediately return the Vehicle to the Lessor’s nominated address with a full fuel tank, clean internally and externally, and with all keys, cards and accessories supplied.' });
  B.push({ kind: 'p', text: '12.2 The Vehicle must be returned in the same condition as received, FAIR WEAR AND TEAR EXCEPTED. Fair wear and tear means minor scuffs and scratches, normal seat and interior wear, and stone chips consistent with ordinary commercial use.' });
  B.push({ kind: 'p', text: '12.3 The following are NOT fair wear and tear and are chargeable to the Lessee: dents and panel damage; cracked or broken lights and mirrors; burns, stains, tears or persistent odours to the interior; missing keys, cards or accessories; and any damage resulting from neglected maintenance or misuse. The Lessor may recover the reasonable cost of rectification from the Lessee.' });
  B.push({ kind: 'p', text: '12.4 The Lessee acknowledges they have NO right to retain the Vehicle and acquire no option to purchase. If the Lessee fails to return the Vehicle, the Lessor may recover it at the Lessee’s expense.' });

  B.push({ kind: 'p', bold: true, text: '13. TERMINATION' });
  B.push({ kind: 'p', text: '13.1 Either party may terminate this Agreement by giving 7 days’ written notice.' });
  B.push({ kind: 'p', text: '13.2 The Lessor may terminate immediately by written notice if the Lessee fails to pay when due; breaches a material term and fails to remedy within 48 hours of notice; has their subcontractor arrangement with Hollypaul Recruitment Pty Ltd terminated; has their licence suspended or cancelled; or where the Lessor determines continued lease poses an unacceptable risk following an At-Fault Incident.' });
  B.push({ kind: 'p', text: '13.3 Upon termination, all amounts owing become immediately due and payable.' });

  B.push({ kind: 'p', bold: true, text: '14. GOVERNING LAW' });
  B.push({ kind: 'p', text: `14.1 This Agreement is governed by the laws of ${state}, and the parties submit to the non-exclusive jurisdiction of the courts of that State.` });

  B.push({ kind: 'p', bold: true, text: '15. ENTIRE AGREEMENT & ACKNOWLEDGEMENT' });
  B.push({ kind: 'p', text: '15.1 This Agreement is the entire agreement between the parties and supersedes all prior negotiations. Any variation must be in writing and signed by both parties.' });
  B.push({ kind: 'p', text: '15.2 The Lessee acknowledges they have had the opportunity to seek independent legal advice, and have read, understood and agree to be bound by all terms of this Agreement.' });

  B.push({ kind: 'rule' });
  B.push({ kind: 'h', text: 'EXECUTED AS AN AGREEMENT' });
  B.push({ kind: 'p', text: 'SIGNED for and on behalf of HOLLYPAUL LEASING PTY LTD:' });
  B.push({ kind: 'sigline', label: 'Signature', value: d.lessorSignature || null });
  B.push({ kind: 'sigline', label: 'Name', value: d.lessorSignatory || null });
  B.push({ kind: 'sigline', label: 'Position', value: d.lessorPosition || null });
  B.push({ kind: 'sigline', label: 'Date', value: d.lessorSignedDate || null });
  B.push({ kind: 'gap' });
  B.push({ kind: 'p', text: hasRep ? `SIGNED by ${lesseeRep} for and on behalf of the LESSEE:` : 'SIGNED by the LESSEE:' });
  B.push({ kind: 'sigline', label: 'Signature', value: d.lesseeSignature || null });
  B.push({ kind: 'sigline', label: 'Name', value: lesseeRep || null });
  if (hasRep) B.push({ kind: 'sigline', label: 'Entity', value: d.lessee?.entity || null });
  B.push({ kind: 'sigline', label: 'ABN', value: d.lessee?.abn || null });
  B.push({ kind: 'sigline', label: 'Date', value: d.lesseeSignedDate || null });

  B.push({ kind: 'rule' });
  B.push({ kind: 'centre', text: 'Generated by the Hollypaul App Engine. Hollypaul Leasing Pty Ltd recommends independent legal review.', size: 7 });

  return B;
}

// Lay the blocks out as a paginated A4 PDF and return the bytes.
export async function buildLeaseContractPdfBytes(data) {
  const blocks = leaseContractBlocks(data);
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const PW = 595.28, PH = 841.89, M = 54, FOOT = 48;
  const colW = PW - M * 2;
  const GOLD = rgb(0.788, 0.659, 0.298);   // #C9A84C
  const INK = rgb(0.067, 0.067, 0.078);    // #111114
  const GREY = rgb(0.5, 0.5, 0.5);

  let page = pdf.addPage([PW, PH]);

  // ── Branded header band (first page) ──
  const BAND = 92;
  page.drawRectangle({ x: 0, y: PH - BAND, width: PW, height: BAND, color: INK });
  page.drawRectangle({ x: 0, y: PH - BAND - 3, width: PW, height: 3, color: GOLD });
  page.drawText('HOLLYPAUL', { x: M, y: PH - 46, size: 25, font: bold, color: GOLD });
  page.drawText('LEASING PTY LTD', { x: M, y: PH - 64, size: 9, font, color: rgb(0.85, 0.85, 0.9) });
  const hTitle = 'VEHICLE LEASE AGREEMENT';
  page.drawText(hTitle, { x: PW - M - bold.widthOfTextAtSize(hTitle, 13), y: PH - 44, size: 13, font: bold, color: rgb(1, 1, 1) });
  const hSub = `Prepared ${fmtDate(data.startDate || new Date())}`;
  page.drawText(hSub, { x: PW - M - font.widthOfTextAtSize(hSub, 8.5), y: PH - 60, size: 8.5, font, color: rgb(0.7, 0.7, 0.78) });

  let y = PH - BAND - 22;

  const ensure = (need) => { if (y - need < FOOT) { page = pdf.addPage([PW, PH]); y = PH - M; } };

  const wrap = (text, f, size, maxW) => {
    const words = String(text).split(/\s+/);
    const lines = [];
    let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (f.widthOfTextAtSize(test, size) > maxW && line) { lines.push(line); line = w; }
      else line = test;
    }
    if (line) lines.push(line);
    return lines;
  };

  const drawText = (text, { f = font, size = 9.5, indent = 0, color = rgb(0.12, 0.12, 0.12), gap = 4, align = 'left' } = {}) => {
    const maxW = colW - indent;
    for (const ln of wrap(text, f, size, maxW)) {
      ensure(size * 1.4);
      let x = M + indent;
      if (align === 'centre') x = M + (colW - f.widthOfTextAtSize(ln, size)) / 2;
      page.drawText(ln, { x, y: y - size, size, font: f, color });
      y -= size * 1.4;
    }
    y -= gap;
  };

  for (const b of blocks) {
    switch (b.kind) {
      case 'title':
        drawText(b.text, { f: bold, size: 15, align: 'centre', gap: 6 });
        break;
      case 'centre':
        drawText(b.text, { f: b.bold ? bold : font, size: b.size || 9.5, align: 'centre', gap: 3 });
        break;
      case 'h':
        ensure(26);
        y -= 8;
        drawText(b.text, { f: bold, size: 11, gap: 3, color: INK });
        // gold accent underline
        page.drawRectangle({ x: M, y: y + 1, width: 38, height: 2, color: GOLD });
        y -= 8;
        break;
      case 'p':
        drawText(b.text, { f: b.bold ? bold : font, size: 9.5, indent: b.indent || 0, gap: b.gap != null ? b.gap : 5 });
        break;
      case 'kv': {
        ensure(15);
        page.drawText(`${b.label}:`, { x: M, y: y - 9.5, size: 9.5, font: bold, color: rgb(0.12, 0.12, 0.12) });
        const vx = M + 150;
        for (const ln of wrap(b.value, font, 9.5, colW - 150)) {
          ensure(13);
          page.drawText(ln, { x: vx, y: y - 9.5, size: 9.5, font, color: rgb(0.12, 0.12, 0.12) });
          y -= 13;
        }
        y -= 2;
        break;
      }
      case 'sigline': {
        ensure(34);
        y -= 10;
        // signature image if provided (data URL PNG)
        if (b.value && typeof b.value === 'string' && b.value.startsWith('data:image')) {
          try {
            const png = await pdf.embedPng(b.value);
            const h = 28, w = (png.width / png.height) * h;
            page.drawImage(png, { x: M + 110, y: y - 24, width: Math.min(w, 200), height: h });
          } catch { /* ignore bad image */ }
        }
        page.drawText(`${b.label}:`, { x: M, y: y - 9, size: 9.5, font, color: rgb(0.12, 0.12, 0.12) });
        page.drawLine({ start: { x: M + 110, y: y - 12 }, end: { x: M + 330, y: y - 12 }, thickness: 0.6, color: rgb(0.5, 0.5, 0.5) });
        if (b.value && !String(b.value).startsWith('data:image')) {
          page.drawText(String(b.value), { x: M + 114, y: y - 9, size: 9.5, font, color: rgb(0.12, 0.12, 0.12) });
        }
        y -= 20;
        break;
      }
      case 'rule':
        ensure(12);
        y -= 4;
        page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.8, color: rgb(0.7, 0.7, 0.7) });
        y -= 10;
        break;
      case 'gap':
        y -= 10;
        break;
      default:
        break;
    }
  }

  // ── Footers: thin rule + identity + page numbers on every page ──
  const pages = pdf.getPages();
  const total = pages.length;
  pages.forEach((pg, i) => {
    pg.drawLine({ start: { x: M, y: 38 }, end: { x: PW - M, y: 38 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
    pg.drawText('HOLLYPAUL LEASING PTY LTD · Vehicle Lease Agreement', { x: M, y: 27, size: 7, font, color: GREY });
    const pn = `Page ${i + 1} of ${total}`;
    pg.drawText(pn, { x: PW - M - font.widthOfTextAtSize(pn, 7), y: 27, size: 7, font, color: GREY });
  });

  return pdf.save();
}

// Append one PDF's pages onto another (e.g. attach the condition report to the
// lease so they're sent/signed as a single document). Returns the merged bytes.
export async function appendPdf(baseBytes, extraBytes) {
  const base = await PDFDocument.load(baseBytes);
  const extra = await PDFDocument.load(extraBytes);
  const pages = await base.copyPages(extra, extra.getPageIndices());
  pages.forEach((p) => base.addPage(p));
  return base.save();
}

// base64 → Uint8Array (browser).
export function base64ToBytes(b64) {
  const bin = atob(b64 || '');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// Convenience: Uint8Array → base64 (for upload payloads), chunked to avoid call-stack limits.
export function bytesToBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
